import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  AutoComplete,
  message,
  Popconfirm,
  Typography,
  InputNumber,
  DatePicker,
  Statistic,
  Row,
  Col,
  Image,
  Switch,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RiseOutlined,
  FallOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { tradesAPI, getImageUrl, getQuoteStreamUrl } from '../services/api';
import { US_STOCK_OPTIONS } from '../data/usStockSymbols';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

/** 管理端行情 HTTP 轮询（与前台同源逻辑，ms 级可调） */
const ADMIN_QUOTE_HTTP_MS = Math.max(200, parseInt(process.env.REACT_APP_QUOTE_HTTP_POLL_MS || '300', 10));

/** 管理端不读浏览器缓存：进入页面后展示一律来自服务端最新拉取，避免「上次留下来的旧价」误导 */
const LIVE_QUOTE_STORAGE_PREFIX = 'gary-strategy:liveQuote:v1:';

/** 启动时清理一次（确保之前留在 localStorage 的旧价不会再被读取或残留） */
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.indexOf(LIVE_QUOTE_STORAGE_PREFIX) === 0) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
  }
} catch (_) {
  /* ignore */
}

/** 可展示价：优先最新价；停盘/仅昨收时用 preClose（与后台 toApiPayload 一致） */
function effectiveQuotePrice(pdata) {
  if (!pdata || pdata.error) return NaN;
  const p = pdata.price != null && pdata.price !== '' ? parseFloat(pdata.price) : NaN;
  if (!Number.isNaN(p) && p > 0) return p;
  const pc = pdata.preClose != null && pdata.preClose !== '' ? parseFloat(pdata.preClose) : NaN;
  if (!Number.isNaN(pc) && pc > 0) return pc;
  if (!Number.isNaN(p)) return p;
  return NaN;
}

function effectiveChangePercent(pdata) {
  if (!pdata || pdata.error) return null;
  if (pdata.changePercent != null && pdata.changePercent !== '') {
    const raw = parseFloat(pdata.changePercent);
    if (!Number.isNaN(raw)) return raw;
  }
  const pre = pdata.preClose != null && pdata.preClose !== '' ? parseFloat(pdata.preClose) : NaN;
  const px = effectiveQuotePrice(pdata);
  if (Number.isNaN(pre) || pre <= 0 || Number.isNaN(px) || px <= 0) return null;
  return ((px - pre) / pre) * 100;
}

const quotePayloadValid = (pdata) => {
  const e = effectiveQuotePrice(pdata);
  return !!(pdata && !pdata.error && !Number.isNaN(e) && e > 0);
};

/** 管理端不再向浏览器持久化报价；保留空实现避免改动其他调用点。 */
const persistQuoteSuccess = () => {};

/** 当前页用到的代码：只保留内存里上一轮真实报价，空缺位置一律等待服务端返回，不再读 localStorage */
const buildInitialRealtimeMap = (prev, activeSymbols) => {
  const upperActive = [...new Set((activeSymbols || []).map((s) => String(s || '').trim().toUpperCase()).filter(Boolean))];
  const activeSet = new Set(upperActive);
  const merged = {};
  for (const k of Object.keys(prev)) {
    const sym = k.toUpperCase();
    if (activeSet.has(sym)) merged[sym] = prev[k];
  }
  return merged;
};

/** 合并行情：新请求里失败或无效的条目保留上一轮（内存或已注入的缓存），避免整表替换后退回库里的 0；
 *  「上次更新」时间戳优先用后端权威字段 `_cachedAt`（这份数据进入后端缓存的时间），
 *   - 盘中：每只票每 30s 一周期，精确反映「后端最后一次问澎博」的时间
 *   - 盘外：后端不再去远端，_cachedAt 停在最后一次拉取时刻
 *   - 没有 _cachedAt 时回退到客户端逻辑（价格变化时重置） */
const mergeRealtimePrices = (prev, incoming, activeSymbols) => {
  const upperActive = [...new Set((activeSymbols || []).map((s) => String(s || '').trim().toUpperCase()).filter(Boolean))];
  const activeSet = new Set(upperActive);
  const merged = {};
  for (const k of Object.keys(prev)) {
    const sym = k.toUpperCase();
    if (activeSet.has(sym)) merged[sym] = prev[k];
  }
  if (!incoming || typeof incoming !== 'object') return merged;
  for (const rawKey of Object.keys(incoming)) {
    const sym = String(rawKey || '').trim().toUpperCase();
    if (!activeSet.has(sym)) continue;
    const pdata = incoming[rawKey];
    if (!pdata || pdata.error) continue;
    const e = effectiveQuotePrice(pdata);
    if (Number.isNaN(e) || e <= 0) continue;
    const prevRow = merged[sym];
    const prevPx = prevRow && prevRow.price != null ? parseFloat(prevRow.price) : NaN;
    const nextPxStr = e.toFixed(2);
    // 优先用后端给的 _cachedAt（精确反映 30s 拉取周期）
    let tickTs = null;
    if (pdata._cachedAt) {
      tickTs = pdata._cachedAt;
    } else {
      tickTs = prevRow && prevRow._tickTs ? prevRow._tickTs : null;
      if (!Number.isNaN(prevPx) && prevPx.toFixed(2) !== nextPxStr) {
        tickTs = Date.now();
      } else if (tickTs == null) {
        tickTs = Date.now();
      }
    }
    merged[sym] = { ...pdata, symbol: sym, price: nextPxStr, _tickTs: tickTs };
  }
  return merged;
};

/** 把毫秒时间戳格式化成 "刚刚" / "5s前" / "2m前" / "1h前" 等友好文案 */
function formatAgo(ts) {
  if (!ts) return '';
  const diff = Math.max(0, Date.now() - ts);
  if (diff < 3000) return '刚刚';
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s 前`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m 前`;
  return `${Math.floor(diff / 3_600_000)}h 前`;
}

const Trades = () => {
  const [loading, setLoading] = useState(false);
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTrade, setEditingTrade] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [realTimePrices, setRealTimePrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [agoTick, setAgoTick] = useState(0);
  const quoteWsRef = useRef(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTrades();
    fetchStats();
  }, [pagination.current, pagination.pageSize]);

  /** 每秒驱动一次重渲染，让「上次更新 5s 前」这种文案不会卡住 */
  useEffect(() => {
    const t = window.setInterval(() => setAgoTick((x) => (x + 1) % 1_000_000), 1000);
    return () => window.clearInterval(t);
  }, []);

  const activeSymbols = useMemo(
    () => [
      ...new Set(
        trades
          .filter((t) => t.status === 'active')
          .map((t) => String(t.symbol || '').trim().toUpperCase())
          .filter(Boolean)
      ),
    ],
    [trades]
  );

  // 须在 paint 前注入浏览器缓存的行情，避免刷页后先有 $0 / 占位再闪出真实数字（不写数据库）
  useLayoutEffect(() => {
    if (activeSymbols.length === 0) {
      setRealTimePrices({});
      setPricesLoading(false);
      return;
    }
    setRealTimePrices((prev) => buildInitialRealtimeMap(prev, activeSymbols));
  }, [activeSymbols]);

  // WebSocket 实时推送 + 定时 HTTP 兜底
  useEffect(() => {
    if (activeSymbols.length === 0) {
      if (quoteWsRef.current) {
        quoteWsRef.current.close();
        quoteWsRef.current = null;
      }
      return undefined;
    }

    let pollSeq = 0;

    const fetchRealTimePrices = async () => {
      const isFirst = pollSeq === 0;
      pollSeq += 1;
      if (isFirst) setPricesLoading(true);
      try {
        const response = await tradesAPI.getStockPrices(activeSymbols);
        if (response.data.success && response.data.data) {
          setRealTimePrices((prev) => {
            const next = mergeRealtimePrices(prev, response.data.data, activeSymbols);
            for (const sym of Object.keys(next)) {
              const d = next[sym];
              if (quotePayloadValid(d) && d._quoteSource !== 'storage')
                persistQuoteSuccess(sym, d);
            }
            return next;
          });
        }
      } catch (error) {
        console.error('Admin fetch stock prices error:', error);
      } finally {
        if (isFirst) setPricesLoading(false);
      }
    };

    fetchRealTimePrices();

    const wsUrl = getQuoteStreamUrl();
    let reconnectTimer;
    let stopped = false;

    const connectWs = () => {
      if (stopped) return;
      try {
        const ws = new WebSocket(wsUrl);
        quoteWsRef.current = ws;
        ws.onopen = () => {
          ws.send(JSON.stringify({ action: 'subscribe', symbols: activeSymbols }));
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === 'quote' && msg.data && msg.data.symbol) {
              const sym = String(msg.data.symbol || '').trim().toUpperCase();
              setRealTimePrices((prev) => {
                const patch = { [sym]: { ...msg.data, symbol: sym } };
                const next = mergeRealtimePrices(prev, patch, activeSymbols);
                const d = next[sym];
                if (quotePayloadValid(d) && d._quoteSource !== 'storage')
                  persistQuoteSuccess(sym, d);
                return next;
              });
            }
          } catch (e) {
            console.warn('Admin quote WS', e);
          }
        };
        ws.onclose = () => {
          if (stopped) return;
          quoteWsRef.current = null;
          reconnectTimer = setTimeout(connectWs, 3000);
        };
        ws.onerror = () => {
          try {
            ws.close();
          } catch (_) {
            /* noop */
          }
        };
      } catch (e) {
        console.error('Admin quote WebSocket', e);
        reconnectTimer = setTimeout(connectWs, 5000);
      }
    };

    connectWs();

    const httpTimer = setInterval(fetchRealTimePrices, ADMIN_QUOTE_HTTP_MS);

    return () => {
      stopped = true;
      clearInterval(httpTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (quoteWsRef.current) {
        quoteWsRef.current.close();
        quoteWsRef.current = null;
      }
    };
  }, [activeSymbols.join('|')]);

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const response = await tradesAPI.getAll({
        page: pagination.current,
        limit: pagination.pageSize
      });
      if (response.data.success) {
        setTrades(response.data.data.trades);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total
        }));
      }
    } catch (error) {
      message.error('获取交易列表失败');
      console.error('Fetch trades error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await tradesAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Fetch stats error:', error);
    }
  };

  const handleAdd = () => {
    setEditingTrade(null);
    form.resetFields();
    setUploadedImageUrl('');
    form.setFieldsValue({ status: 'active', market: 'NASDAQ', isPublic: true });
    setModalVisible(true);
  };

  const handleEdit = (trade) => {
    setEditingTrade(trade);
    // 确保 image 是字符串
    const imageUrl = typeof trade.image === 'string' ? trade.image : (trade.image || '');
    setUploadedImageUrl(imageUrl);
    form.setFieldsValue({
      ...trade,
      image: imageUrl, // 明确设置为字符串
      entryDate: trade.entryDate ? dayjs(trade.entryDate) : null,
      exitDate: trade.exitDate ? dayjs(trade.exitDate) : null,
    });
    setModalVisible(true);
  };

  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const response = await tradesAPI.uploadImage(file);
      if (response.data.success) {
        const imageUrl = response.data.data.imageUrl;
        // 同时更新表单字段和状态，确保UI立即更新
        form.setFieldsValue({ image: imageUrl });
        setUploadedImageUrl(imageUrl);
        message.success('图片上传成功');
        return imageUrl;
      } else {
        message.error('图片上传失败');
        return false;
      }
    } catch (error) {
      message.error('图片上传失败: ' + (error.response?.data?.message || error.message));
      console.error('Upload error:', error);
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleImageRemove = () => {
    form.setFieldsValue({ image: '' });
    setUploadedImageUrl('');
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      
      // 确保 image 字段是字符串
      const imageValue = values.image || uploadedImageUrl || '';
      const imageUrlStr = typeof imageValue === 'string' ? imageValue : String(imageValue || '').trim();
      
      // 清理数据，确保格式正确
      const submitData = {
        symbol: (values.symbol || '').trim().toUpperCase(),
        market: values.market || 'NASDAQ',
        entryPrice: parseFloat(values.entryPrice) || 0,
        exitPrice: values.exitPrice ? parseFloat(values.exitPrice) : null,
        currentPrice: values.currentPrice ? parseFloat(values.currentPrice) : null,
        quantity: parseInt(values.quantity) || 0,
        status: values.status || 'active',
        entryDate: values.entryDate ? values.entryDate.toISOString() : null,
        exitDate: values.exitDate ? values.exitDate.toISOString() : null,
        notes: values.notes || '',
        image: imageUrlStr, // 确保是字符串
        isPublic: values.isPublic !== undefined ? values.isPublic : true,
      };
      
      if (editingTrade) {
        await tradesAPI.update(editingTrade.id, submitData);
        message.success('交易更新成功');
      } else {
        await tradesAPI.create(submitData);
        message.success('交易创建成功');
      }
      
      setModalVisible(false);
      setUploadedImageUrl('');
      form.resetFields();
      fetchTrades();
      fetchStats();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || '操作失败';
      message.error(`操作失败: ${errorMsg}`);
      console.error('Submit error:', error);
      if (error.response?.data) {
        console.error('Error details:', error.response.data);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await tradesAPI.delete(id);
      message.success('交易已删除');
      fetchTrades();
      fetchStats();
    } catch (error) {
      message.error('删除失败');
      console.error('Delete error:', error);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const formatPrice = (price) => {
    const num = parseFloat(price);
    return !isNaN(num) ? `$${num.toFixed(2)}` : '-';
  };

  const formatRatio = (ratio) => {
    const num = parseFloat(ratio);
    return !isNaN(num) ? num.toFixed(2) : '0.00';
  };

  const getLivePrice = (trade) => {
    if (trade.status !== 'active') return null;
    const sym = (trade.symbol || '').toUpperCase();
    const pdata = realTimePrices[sym];
    const e = effectiveQuotePrice(pdata);
    return Number.isNaN(e) ? null : e;
  };

  /** 持仓：报价/缓存优先；库 currentPrice 仅在大于 0 时作兜底（避免库里占位 0 盖住浏览器缓存） */
  const getDisplayCurrentOrExitPrice = (trade) => {
    if (trade.status === 'active') {
      const live = getLivePrice(trade);
      const db = parseFloat(trade.currentPrice);
      if (live != null && !Number.isNaN(live) && live > 0) return live;
      if (!Number.isNaN(db) && db > 0) return db;
      if (live != null && !Number.isNaN(live)) return live;
      return null;
    }
    const ex = parseFloat(trade.exitPrice);
    return !isNaN(ex) ? ex : null;
  };

  const columns = [
    {
      title: '股票代码',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (symbol) => <Text strong>{symbol}</Text>,
    },
    {
      title: '市场',
      dataIndex: 'market',
      key: 'market',
      render: (market) => <Tag>{market}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={status === 'active' ? 'blue' : 'green'}>
            {status === 'active' ? '持仓中' : '已平仓'}
          </Tag>
          {record.statusLabel && <small>{record.statusLabel}</small>}
        </Space>
      ),
    },
    {
      title: '入场价',
      dataIndex: 'entryPrice',
      key: 'entryPrice',
      render: (price) => formatPrice(price),
    },
    {
      title: (
        <span>
          当前/出场价
          {pricesLoading ? (
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 6, fontWeight: 'normal' }}>
              （行情刷新中…）
            </Text>
          ) : null}
        </span>
      ),
      key: 'price',
      render: (_, record) => {
        const sym = (record.symbol || '').toUpperCase();
        const pdataActive = record.status === 'active' ? realTimePrices[sym] : null;
        const price = getDisplayCurrentOrExitPrice(record);
        const live = record.status === 'active' ? getLivePrice(record) : null;
        const fromApi = live != null && pdataActive;
        const source = pdataActive && pdataActive._quoteSource ? pdataActive._quoteSource : null;
        // psbangu = WS 秒级实时；psbangu_http = HTTP 准实时（30s 缓存，澎博官方数据）
        const isPsBanguWs = fromApi && (source === 'psbangu' || source == null);
        const isPsBanguHttp = fromApi && source === 'psbangu_http';
        const isPsBangu = isPsBanguWs || isPsBanguHttp;
        const isEastmoney = fromApi && source === 'eastmoney';
        const isSina = fromApi && source === 'sina';
        const isFinnhub = fromApi && source === 'finnhub';
        const isLongbridge = fromApi && source === 'longbridge';
        const isYahoo = fromApi && source === 'yahoo';
        const fromDb = !fromApi && record.status === 'active' && parseFloat(record.currentPrice) > 0;
        const chRaw =
          record.status === 'active' && pdataActive ? effectiveChangePercent(pdataActive) : null;
        const ch = chRaw != null ? parseFloat(chRaw) : NaN;
        const tickTs = pdataActive && pdataActive._tickTs ? pdataActive._tickTs : null;

        if (price == null || isNaN(price)) return '-';

        return (
          <Space direction="vertical" size={0}>
            <Space size={6} wrap>
              <Text strong>{formatPrice(price)}</Text>
              {isFinnhub ? (
                <Tag color="green" style={{ margin: 0 }} title="Finnhub 实时（IEX 数据源，盘中毫秒级）">
                  实时(Finnhub)
                </Tag>
              ) : null}
              {isLongbridge ? (
                <Tag color="processing" style={{ margin: 0 }} title="Longbridge OpenAPI 实时推送">
                  实时(LB)
                </Tag>
              ) : null}
              {isYahoo ? (
                <Tag color="purple" style={{ margin: 0 }} title="Yahoo Finance chart（非官方），仅供参考对比波动">
                  Yahoo
                </Tag>
              ) : null}
              {isPsBangu ? (
                <Tag
                  color="processing"
                  style={{ margin: 0 }}
                  title={isPsBanguWs
                    ? '澎博 WebSocket 实时推送（秒级）'
                    : '澎博 HTTP（≤10s 延时，与官方同源）'}
                >
                  实时
                </Tag>
              ) : null}
              {isEastmoney ? (
                <Tag color="cyan" style={{ margin: 0 }} title="东方财富美股兜底（主源不支持此票），数据滞后约 1-3 分钟">
                  准实时(东财)
                </Tag>
              ) : null}
              {isSina ? (
                <Tag color="default" style={{ margin: 0 }} title="新浪美股兜底（主源不支持此票），数据滞后约 15 分钟">
                  延时 15min
                </Tag>
              ) : null}
              {fromDb ? (
                <Tag color="warning" style={{ margin: 0 }}>
                  手填
                </Tag>
              ) : null}
            </Space>
            {record.status === 'active' && !isNaN(ch) ? (
              <Text type={ch >= 0 ? 'success' : 'danger'} style={{ fontSize: 12 }}>
                {ch >= 0 ? '▲' : '▼'} {Math.abs(ch).toFixed(2)}%
              </Text>
            ) : null}
            {isPsBangu && tickTs ? (
              <Text type="secondary" style={{ fontSize: 11 }} title={new Date(tickTs).toLocaleString()}>
                上次更新 {formatAgo(tickTs)}
              </Text>
            ) : null}
            {isEastmoney ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                东方财富兜底（≈1-3min 延时）
              </Text>
            ) : null}
            {isSina ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                新浪兜底（≈15min 延时）
              </Text>
            ) : null}
            {!fromApi && record.status === 'active' ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {fromDb ? '澎博/兜底均无该票，显示数据库当前价' : '澎博/兜底暂无该票实时行情'}
              </Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (qty) => {
        const num = parseInt(qty, 10);
        return !isNaN(num) ? num.toLocaleString() : '0';
      },
    },
    {
      title: '盈亏比例',
      dataIndex: 'pnlRatio',
      key: 'pnlRatio',
      render: (ratio, record) => {
        let num;
        if (record.status === 'active') {
          const entry = parseFloat(record.entryPrice) || 0;
          const cur = getDisplayCurrentOrExitPrice(record);
          if (entry > 0 && cur != null && !isNaN(cur)) {
            num = ((cur - entry) / entry) * 100;
          } else {
            num = parseFloat(ratio) || 0;
          }
        } else {
          num = parseFloat(ratio) || 0;
        }
        return (
          <Text type={num >= 0 ? 'success' : 'danger'}>
            {num >= 0 ? <RiseOutlined /> : <FallOutlined />} {formatRatio(num)}%
          </Text>
        );
      },
    },
    {
      title: '盈亏金额',
      dataIndex: 'pnlAmount',
      key: 'pnlAmount',
      render: (amount, record) => {
        let num;
        if (record.status === 'active') {
          const entry = parseFloat(record.entryPrice) || 0;
          const cur = getDisplayCurrentOrExitPrice(record);
          const qty = parseInt(record.quantity, 10) || 0;
          if (entry > 0 && cur != null && !isNaN(cur)) {
            num = (cur - entry) * qty;
          } else {
            num = parseFloat(amount) || 0;
          }
        } else {
          num = parseFloat(amount) || 0;
        }
        return (
          <Text type={num >= 0 ? 'success' : 'danger'} strong>
            {num >= 0 ? '+' : ''}{num.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
          </Text>
        );
      },
    },
    {
      title: '入场时间',
      dataIndex: 'entryDate',
      key: 'entryDate',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: '图片',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (imageUrl) => {
        const fullUrl = getImageUrl(imageUrl);
        if (!fullUrl) return '-';
        
        return (
          <Image
            width={60}
            height={60}
            src={fullUrl}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={{ src: fullUrl }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBuyhJLqWgQGLcYYzAHj4uYkxmQMxBBgZ2NjYF4CBitILEoEe4Axm8sxWnGRhA2j+3AwMzJ8IsAcJqB4dWf4//9xZGBgx0jwMC+geCv4vA4M8v"
          />
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>首页交易记录配置</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加交易
        </Button>
      </div>

      {/* Statistics */}
      <Row gutter={16}>
        <Col span={6}>
          <Card><Statistic title="总交易次数" value={stats.totalTrades || 0} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="活跃交易" value={stats.activeTrades || 0} valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="胜率" value={stats.winRate || 0} suffix="%" valueStyle={{ color: '#52c41a' }} precision={2} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="总盈利" value={stats.totalProfit || 0} prefix="$" precision={2} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={trades}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          locale={{ emptyText: '暂无交易数据' }}
        />
      </Card>

      <Modal
        title={editingTrade ? '编辑交易' : '添加交易'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          setUploadedImageUrl('');
        }}
        width={700}
        okText="保存"
        cancelText="取消"
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="symbol"
                label="股票代码"
                extra="可搜索选常用美股；不在列表里也可手动输入代码后保存（持仓中才会拉实时价）。"
                rules={[
                  { required: true, message: '请选择或输入股票代码' },
                  {
                    pattern: /^[A-Za-z0-9.\-_]+$/,
                    message: '仅允许字母、数字及 . - _',
                  },
                ]}
              >
                <AutoComplete
                  options={US_STOCK_OPTIONS}
                  allowClear
                  placeholder="搜索或输入，如 NVDA / AAPL"
                  filterOption={(inputValue, option) => {
                    if (!inputValue) return true;
                    const q = inputValue.trim().toUpperCase();
                    const v = String(option?.value ?? '').toUpperCase();
                    const l = String(option?.label ?? '').toUpperCase();
                    return v.includes(q) || l.includes(q);
                  }}
                  onSelect={(_val, option) => {
                    if (option?.exchange) {
                      form.setFieldsValue({ market: option.exchange });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="market" label="市场" rules={[{ required: true, message: '请选择市场' }]}>
                <Select>
                  <Option value="NASDAQ">NASDAQ</Option>
                  <Option value="NYSE">NYSE</Option>
                  <Option value="AMEX">AMEX</Option>
                  <Option value="OTC">OTC</Option>
                  <Option value="USA">USA</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
                <Select>
                  <Option value="active">持仓中</Option>
                  <Option value="closed">已平仓</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="entryPrice" label="入场价" rules={[{ required: true, message: '请输入入场价' }]}>
                <InputNumber style={{ width: '100%' }} prefix="$" precision={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currentPrice" label="当前价">
                <InputNumber style={{ width: '100%' }} prefix="$" precision={2} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="exitPrice" label="出场价">
                <InputNumber style={{ width: '100%' }} prefix="$" precision={2} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="quantity" label="数量" rules={[{ required: true, message: '请输入数量' }]}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="entryDate" label="入场时间" rules={[{ required: true, message: '请选择入场时间' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="exitDate" label="出场时间">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="备注">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="isPublic"
            label="在前台网站展示"
            valuePropName="checked"
            tooltip="关闭后，未登录管理员账号的前台用户将看不到这条交易"
          >
            <Switch checkedChildren="展示" unCheckedChildren="隐藏" />
          </Form.Item>
          <Form.Item 
            name="image" 
            label="交易图片"
            extra="支持上传图片文件（jpeg, jpg, png, gif, webp），最大5MB"
            valuePropName="value"
            getValueFromEvent={() => {
              const imageValue = uploadedImageUrl || form.getFieldValue('image') || '';
              return typeof imageValue === 'string' ? imageValue : '';
            }}
          >
            <Input type="hidden" />
          </Form.Item>
          <Form.Item label=" " colon={false}>
            <Upload
              listType="picture-card"
              maxCount={1}
              showUploadList={false}
              beforeUpload={async (file) => {
                await handleImageUpload(file);
                return false; // 阻止自动上传
              }}
              accept="image/*"
            >
              {(() => {
                const imageValue = uploadedImageUrl || form.getFieldValue('image') || '';
                const imageUrlStr = typeof imageValue === 'string' ? imageValue : '';
                return imageUrlStr ? (
                  <img
                    src={getImageUrl(imageUrlStr)}
                    alt="交易图片"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                    onError={(e) => {
                      console.error('图片加载失败:', e.target.src);
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div>
                    <div>{uploading ? '上传中...' : <UploadOutlined />}</div>
                    <div style={{ marginTop: 8 }}>上传图片</div>
                  </div>
                );
              })()}
            </Upload>
            {(() => {
              const imageValue = uploadedImageUrl || form.getFieldValue('image') || '';
              const imageUrlStr = typeof imageValue === 'string' ? imageValue : '';
              return imageUrlStr ? (
                <Button 
                  type="link" 
                  danger 
                  onClick={handleImageRemove}
                  style={{ marginTop: 8 }}
                >
                  删除图片
                </Button>
              ) : null;
            })()}
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default Trades;
